import React, { Component } from 'react'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { withRouter } from 'common/utils/withRouter'
import _ from 'lodash'
import Well from '../common/Well'
import FormSection from '../common/FormSection'
import RetailerSearch from './form/RetailerSearch'
import DiamondStatus from './form/DiamondStatus'
import RequiredInfo from './form/RequiredInfo'
import OptionalInfo from './form/OptionalInfo'
import JewelryInfo from './form/JewelryInfo'
import DiamondImages from './form/DiamondImages'
import Comments from './form/Comments'
import {
  getDiamond,
  createDiamond,
  saveDiamond,
  deleteDiamond,
  deleteDiamondImage,
  clearDiamondFieldError
} from '../../actions/diamond'
import { showFullscreenLoader, hideFullscreenLoader } from '../../actions/form'
import {
  handleFieldChange,
  withGallery,
  FormErrorMessage
} from '../../utils/form'
import {
  statusCodes,
  statusCodeToText,
  statusTextToCode
} from '../../utils/diamond'
import { validateDiamond } from '../../utils/validators'
import { withSavedFlag } from '../../utils/savedMessage'

import styles from './Form.module.css'

// Mirrors the numeric parsing done in mapDispatchToProps before calling saveDiamond,
// so real-time validateDiamond calls receive proper numbers instead of strings.
function parseNumericFields(data) {
  const d = { ...data }
  const price = _.isString(d.price)
    ? parseFloat(d.price.replace(/\$|,/g, ''))
    : d.price
  d.price = isNaN(price) ? d.price : price
  let carat = d.carat
  if (carat === 'not-applicable') {
    carat = -1
  }
  carat = parseFloat(carat)
  d.carat = isNaN(carat) ? d.carat : carat
  ;['propLength', 'propWidth', 'propDepth', 'markup', 'salePrice',
    'mountSize', 'totalWeightCarat', 'totalWeightGrams'].forEach(field => {
    const v = parseFloat(d[field])
    d[field] = isNaN(v) ? d[field] : v
  })
  return d
}

class DiamondForm extends Component {
  constructor(...args) {
    super(...args)
    this.state = null
    this.handleChange = this.handleFieldChange.bind(this)
    this.handleSettingChange = this.handleSettingChange.bind(this)
    this.handleRetailerSelect = this.handleRetailerSelect.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
    this.handleDelete = this.handleDelete.bind(this)
  }
  componentDidMount() {
    const {
      routeParams: { diamondId },
      getDiamond,
      showFullscreenLoader,
      hideFullscreenLoader
    } = this.props
    showFullscreenLoader()
    getDiamond(diamondId)
      .finally(hideFullscreenLoader)
  }
  componentDidUpdate(prevProps) {
    const { diamond, hideFullscreenLoader, images } = this.props

    // Once the form is initialised, keep firstImagePath in sync with the gallery
    // so that removing a URL-based image immediately updates the field used by
    // validation. Only URL images that have no pending file replacement are kept.
    if (this.state && prevProps.images !== images) {
      const remainingUrls = (images || [])
        .filter(img => !img.file && img.attributes && img.attributes.url)
        .map(img => img.attributes.url)
      const hasFile = (images || []).some(img => img.file)
      this.setState(prev => {
        const nextValidationErrors = { ...prev.validationErrors }
        if (remainingUrls.length > 0 || hasFile) {
          delete nextValidationErrors.images
        }
        return {
          firstImagePath: remainingUrls[0] || '',
          validationErrors: nextValidationErrors
        }
      })
      return
    }

    if (!diamond || this.state) {
      return
    }
    hideFullscreenLoader()
    const state = _.clone(diamond.attributes)
    state.statusCode = parseInt(state.statusCode)
    state.status = statusCodeToText(state.statusCode)
    state.validationErrors = {}
    state.touched = {}
    // The status the item was loaded with. Picking a radio rewrites
    // state.statusCode immediately, so "is this item sold" — the question that
    // decides whether Available stays closed and whether Sold can be picked
    // back — has to be answered from the persisted value rather than the live
    // selection. Held on the instance, not in state, so it is never submitted
    // as an item attribute.
    this.loadedStatusCode = state.statusCode
    this.setState(state)
  }
  _getImagesForValidation() {
    // Include both newly-selected files and still-present URL images so that
    // removing one of several URL images does not falsely trigger the
    // "at least one image required" error.
    return (this.props.images || []).filter(
      img => img.file || (!img.file && img.attributes && img.attributes.url)
    )
  }
  render() {
    if (!this.state) {
      return null
    }
    const {
      routeParams,
      router,
      diamond,
      inProgress,
      error,
      isAdmin,
      isVerifiedSeller,
      cancelButtonDisabled,
      renderError
    } = this.props
    const { diamondId } = routeParams
    const title = `${diamondId ? 'Edit' : 'Add'} an Item`
    const isSold = this.state.statusCode === statusCodes.SOLD.value
    const isDeleted = this.state.statusCode === statusCodes.DELETED.value
    const canDelete = diamondId && !isDeleted && !isSold
    // Merge real-time local validation errors with Redux errors (Redux takes precedence)
    const fieldErrors = Object.assign(
      {},
      this.state.validationErrors || {},
      error || {}
    )
    let submitButtonText
    if (inProgress) {
      submitButtonText = (
        <span>
          <i className="fa fa-circle-o-notch fa-spin" /> Processing...
        </span>
      )
    } else if (diamondId) {
      submitButtonText = 'Save'
    } else {
      submitButtonText = 'Submit'
    }
    return (
      <div id="content">
        <header>
          <h2>
            {title}
          </h2>
        </header>
        <form className={`smart-form ${styles.form}`}>
          <FormErrorMessage
            error={error}
            errorListClassName={styles.errorList}
            onRef={node => {
              this.errorNode = node
            }}
          />
          {isAdmin
            ? <Well>
              <header>Attach Item to Retailer</header>
              <fieldset>
                <RetailerSearch
                  data={this.state}
                  onSelect={this.handleRetailerSelect}
                  disabled={inProgress}
                  error={fieldErrors}
                />
              </fieldset>
            </Well>
            : null}

          <Well>
            <FormSection title="Item Status" open>
              <DiamondStatus
                data={this.state}
                loadedStatusCode={this.loadedStatusCode}
                onChangeFactory={this.handleChange}
                disabled={inProgress}
                isAdmin={isAdmin}
              />
            </FormSection>
          </Well>

          <Well>
            <FormSection title="Required Item Info" open>
              <RequiredInfo
                data={this.state}
                error={fieldErrors}
                onChangeFactory={this.handleChange}
                onSettingChange={this.handleSettingChange}
                disabled={inProgress}
                isVerifiedSeller={isVerifiedSeller}
              />
            </FormSection>

            <FormSection title="Optional Item Info">
              <OptionalInfo
                data={this.state}
                error={fieldErrors}
                onChangeFactory={this.handleChange}
                disabled={inProgress}
              />
            </FormSection>

            {this.state.setting !== 'loose' &&
              this.state.setting !== 'gemstone' &&
              <FormSection title="Optional Jewelry Info">
                <JewelryInfo
                  data={this.state}
                  error={fieldErrors}
                  onChangeFactory={this.handleChange}
                  disabled={inProgress}
                />
              </FormSection>}

            <FormSection title="Item Images" open>
              <DiamondImages
                images={this.props.images}
                error={fieldErrors}
                onSelectFactory={this.props.handleImageSelect}
                onRemoveFactory={this.props.handleImageRemove.bind(
                  null,
                  diamond
                )}
                onCaptionChangeFactory={this.props.handleImageCaptionChange}
                disabled={inProgress}
              />
            </FormSection>

            <FormSection title="Item Description and Comments">
              <Comments data={this.state} onChangeFactory={this.handleChange} />
            </FormSection>

            <footer>
              <div className={styles.noteWide}>
                A successful "Submit" will redirect<br />
                this form to the inventory screen.
              </div>
              <button
                type="submit"
                className="btn btn-primary"
                onClick={this.handleSubmit}
                disabled={inProgress}
              >
                {submitButtonText}
              </button>
              <button
                type="button"
                className="btn btn-default"
                onClick={() => {
                  router.goBack()
                }}
                disabled={inProgress || cancelButtonDisabled}
              >
                Cancel
              </button>
              {canDelete &&
                <button
                  className="btn btn-danger pull-left"
                  disabled={inProgress}
                  onClick={this.handleDelete}
                >
                  Delete
                </button>}
            </footer>
          </Well>
        </form>
      </div>
    )
  }
  handleRetailerSelect(suggestion) {
    let retailerId = suggestion.objectID
    if (suggestion.createdDay) {
      retailerId = `${suggestion.createdDay}/${retailerId}`
    }
    this.setState({
      retailerName: suggestion.name,
      retailerId
    })
    this.props.clearDiamondFieldError('retailer')
    this.props.clearDiamondFieldError('retailerName')
    this.setState(prevState => {
      const nextValidationErrors = { ...prevState.validationErrors }
      delete nextValidationErrors.retailer
      return {
        touched: { ...prevState.touched, retailer: true },
        validationErrors: nextValidationErrors
      }
    })
  }
  handleFieldChange(fieldName) {
    const onChange = handleFieldChange(this, fieldName)
    return event => {
      // Extract new value to compute next state for real-time validation
      const input = event.target
      let val = input.type === 'checkbox' ? input.checked : input.value
      if (input.type === 'radio') {
        if (val === 'true') val = true
        else if (val === 'false') val = false
      }

      onChange(event)
      this.props.clearDiamondFieldError(fieldName)

      // For proportion fields, the combined error key is 'proportions'
      const isPropField = ['propLength', 'propWidth', 'propDepth'].includes(fieldName)
      if (isPropField) {
        this.props.clearDiamondFieldError('proportions')
      }

      // Real-time validation against the incoming field value
      // Parse numeric fields first so _.isNumber() checks in validateDiamond pass
      const nextData = parseNumericFields({ ...this.state, [fieldName]: val })
      const { error: newErrors } = validateDiamond(nextData, this._getImagesForValidation())

      this.setState(prevState => {
        const nextValidationErrors = { ...prevState.validationErrors }
        if (newErrors[fieldName]) {
          nextValidationErrors[fieldName] = newErrors[fieldName]
        } else {
          delete nextValidationErrors[fieldName]
        }
        if (isPropField) {
          if (newErrors.proportions) {
            nextValidationErrors.proportions = newErrors.proportions
          } else {
            delete nextValidationErrors.proportions
          }
        }
        return {
          touched: { ...prevState.touched, [fieldName]: true },
          validationErrors: nextValidationErrors
        }
      })
    }
  }
  handleSettingChange(event) {
    const setting = event.target.value
    const change = { setting }
    const value =
      setting === 'loose' || setting === 'mounted' ? '' : 'not-applicable'
    Object.assign(change, {
      colorType: value || 'regular',
      shape: value,
      carat: value,
      cut: value,
      clarity: value,
      color: value,
      certificate: value
    })
    this.setState(change)
    const fieldsToClear = [
      'setting',
      'colorType',
      'shape',
      'carat',
      'cut',
      'clarity',
      'color',
      'certificate'
    ]
    fieldsToClear.forEach(this.props.clearDiamondFieldError)
    // Clear local validation errors for all reset fields so stale errors don't flash
    this.setState(prevState => {
      const nextValidationErrors = { ...prevState.validationErrors }
      const nextTouched = { ...prevState.touched }
      fieldsToClear.forEach(f => {
        delete nextValidationErrors[f]
        delete nextTouched[f]
      })
      return { validationErrors: nextValidationErrors, touched: nextTouched }
    })
  }
  handleSubmit(event) {
    event.preventDefault()
    const { router, location, diamond, images, saveDiamond, routeParams } = this.props
    saveDiamond(diamond, this.state, images).then(
      () => {
        const backUrl = location.query.backUrl || '/inventory'
        // The success message is shown on the list we redirect to (that is
        // where the ToastContainer lives), so flag it on the URL — the same
        // approach as the ?loggedOut=true message on the login screen.
        const saved = routeParams.diamondId ? 'updated' : 'created'
        router.push(withSavedFlag(backUrl, saved))
      },
      error => {
        if (this.errorNode) {
          this.errorNode.scrollIntoView({ behavior: 'smooth' })
        }
      }
    )
  }
  handleDelete(event) {
    event.preventDefault()
    const { router, location, diamond, deleteDiamond } = this.props
    deleteDiamond(diamond).then(
      () => {
        const backUrl = location.query.backUrl || '/inventory'
        router.push(backUrl)
      },
      () => {
        if (this.errorNode) {
          this.errorNode.scrollIntoView({ behavior: 'smooth' })
        }
      }
    )
  }
}

function mapStateToProps({ diamond, login, image }, { routeParams }) {
  let selectedDiamond = diamond.get('selectedDiamond')
  let cancelButtonDisabled = false
  const diamondId = routeParams.diamondId
  if (selectedDiamond && (!diamondId || selectedDiamond.id === diamondId)) {
    if (diamondId) {
      const images = selectedDiamond.getRelated('images')
      // cancelButtonDisabled = !images || images.size() < 1
    }
    selectedDiamond = selectedDiamond.toJSON()
  } else {
    selectedDiamond = null
  }
  const currentUser = login.get('currentUser')
  const isAdmin = !!login.get('isAdminUser')
  const isVerifiedSeller = isAdmin || !!(currentUser && (typeof currentUser.get === 'function' ? currentUser.get('verified') : currentUser.verified))
  return {
    diamond: selectedDiamond,
    inProgress: diamond.get('inProgress') || image.get('inProgress'),
    error: diamond.get('error') || {},
    currentUser,
    isAdmin,
    isVerifiedSeller,
    cancelButtonDisabled
  }
}

function mapDispatchToProps(dispatch) {
  return {
    getDiamond(diamondId) {
      const diamond = diamondId
        ? dispatch(getDiamond(diamondId))
        : dispatch(createDiamond())
      return Promise.resolve(diamond)
    },
    saveDiamond(diamond, data, images) {
      const price = _.isString(data.price)
        ? parseFloat(data.price.replace(/\$|,/g, ''))
        : data.price
      let carat = data.carat
      if (carat === 'not-applicable') {
        carat = -1
      }
      carat = parseFloat(carat)
      const propLength = parseFloat(data.propLength)
      const propWidth = parseFloat(data.propWidth)
      const propDepth = parseFloat(data.propDepth)
      const markup = parseFloat(data.markup)
      const salePrice = parseFloat(data.salePrice)
      const mountSize = parseFloat(data.mountSize)
      const totalWeightCarat = parseFloat(data.totalWeightCarat)
      const totalWeightGrams = parseFloat(data.totalWeightGrams)
      data.price = isNaN(price) ? data.price : price
      data.carat = isNaN(carat) ? data.carat : carat
      data.propLength = isNaN(propLength) ? data.propLength : propLength
      data.propWidth = isNaN(propWidth) ? data.propWidth : propWidth
      data.propDepth = isNaN(propDepth) ? data.propDepth : propDepth
      data.markup =
        data.saleMarkupType === 'markup'
          ? isNaN(markup) ? data.markup : markup
          : 0
      data.salePrice =
        data.saleMarkupType === 'sale'
          ? isNaN(salePrice) ? data.salePrice : salePrice
          : 0
      data.mountSize = isNaN(mountSize) ? data.mountSize : mountSize
      data.totalWeightCarat = isNaN(totalWeightCarat)
        ? data.totalWeightCarat
        : totalWeightCarat
      data.totalWeightGrams = isNaN(totalWeightGrams)
        ? data.totalWeightGrams
        : totalWeightGrams
      diamond.attributes = _.omit(data, ['images', 'validationErrors', 'touched'])
      // Pass ALL images (existing + new) to saveDiamond so it can preserve existing URLs
      dispatch(
        showFullscreenLoader('Please wait while we are saving the data...')
      )
      return dispatch(saveDiamond(diamond, images)).finally(() => {
        dispatch(hideFullscreenLoader())
      })
    },
    deleteDiamond(diamond) {
      return dispatch(deleteDiamond(diamond))
    },
    clearDiamondFieldError(fieldName) {
      return dispatch(clearDiamondFieldError(fieldName))
    },
    showFullscreenLoader() {
      return dispatch(showFullscreenLoader())
    },
    hideFullscreenLoader() {
      return dispatch(hideFullscreenLoader())
    }
  }
}

function entitySelector({ diamond }) {
  return diamond.get('selectedDiamond')
}

export default compose(
  withRouter,
  withGallery(entitySelector, deleteDiamondImage),
  connect(mapStateToProps, mapDispatchToProps)
)(DiamondForm)
